import fileType, { FileTypeResult } from "file-type";
import core from "file-type/core";
import mime from "mime-types";
import { getFileExtension } from "./get-file-extension";

const isPlainText = (buffer: Uint8Array) => {
  try {
    // Attempt to decode the buffer using UTF-8
    const text = new TextDecoder("utf-8", { fatal: true }).decode(buffer);
    // If we successfully decode without errors, it's plain text
    return true;
  } catch (error) {
    // If an error occurs during decoding, the content is not valid plain text
    return false;
  }
};

const isPrintableAscii = (buffer: Uint8Array): boolean => {
  for (let i = 0; i < buffer.length; i++) {
    const byte = buffer[i];
    if (
      (byte < 32 && byte !== 9 && byte !== 10 && byte !== 13) || // Non-printable ASCII except common whitespace (tab, newline, carriage return)
      byte > 126
    ) {
      return false;
    }
  }
  return true;
};

const isSVG = (buffer: Uint8Array): boolean => {
  const decoder = new TextDecoder("utf-8");
  const text = decoder.decode(buffer.slice(0, 100)).trim(); // Decode the first 100 bytes and trim whitespace
  // Check if it starts with <?xml or <svg
  return text.startsWith("<?xml") || text.startsWith("<svg");
};

export const checkFileMimeType = async (file: File) => {
  const fileExtension: string = getFileExtension(file.name);
  const arrayBuffer: ArrayBuffer = await file.arrayBuffer();
  const buffer: Uint8Array = new Uint8Array(arrayBuffer);
  const type: core.FileTypeResult | undefined = await fileType.fromBuffer(buffer);

  // txt and svg should be checked differently because type will be undefined
  if (fileExtension === "txt") {
    return isPlainText(buffer) && isPrintableAscii(buffer);
  }

  if (fileExtension === "svg") {
    return isSVG(buffer);
  }

  if (type) {
    const expectedMimeType = mime.lookup(file.name) || null;
    return expectedMimeType === type.mime;
  }
  return false;
};

/***
 * Will return undefined for SVG and txt
 * @param file 
 * @returns boolean
 
 **/
export const getFileMimeType = async (file: File): Promise<FileTypeResult | undefined> => {
  const arrayBuffer: ArrayBuffer = await file.arrayBuffer();
  const buffer: Uint8Array = new Uint8Array(arrayBuffer);
  const type: core.FileTypeResult | undefined = await fileType.fromBuffer(buffer);

  return type;
};
